/**
 * Synthetic example data for the route table, set in the demo campaign "The Drowned Lanterns".
 * The mocks answer with these values, and the sweeps build their requests from them.
 * Nothing here is real campaign content or a real credential.
 */
import { resolveFlags } from '../registries/flags.js';
/** The fixed test epoch (P0-12 `TEST_EPOCH`). */
export const EXAMPLE_NOW = '2026-09-12T19:00:00.000Z';
/**
 * Demo credentials. The mock's `auth.login` accepts exactly these, and `auth.login`'s example
 * request carries them, so a parity run can create the same user on the real server.
 */
export const DEMO_CREDENTIALS = Object.freeze({
    username: 'gm',
    password: 'drowned-lanterns-demo',
});
/** The mock's `auth.me` answer: every flag present, `design` on. */
export const EXAMPLE_ME = {
    username: DEMO_CREDENTIALS.username,
    scope: 'gm',
    expiresAt: '2026-10-12T19:00:00.000Z',
    flags: { ...resolveFlags({}, { mode: 'production' }), design: true },
};
/**
 * `derived_state` rows as `HealthV1.derived` carries them (CS r1c): one verified clean, one with
 * drift, one never verified or rebuilt, and one whose deriver no longer exists.
 */
export const EXAMPLE_DERIVED_STATE = [
    { name: 'body', deriverVersion: 1, status: 'ok', driftCount: 0, rebuiltAt: EXAMPLE_NOW },
    { name: 'names', deriverVersion: 2, status: 'drift', driftCount: 3, rebuiltAt: EXAMPLE_NOW },
    { name: 'fields', deriverVersion: 1, status: null, driftCount: 0, rebuiltAt: null },
    { name: 'retired_counts', deriverVersion: 1, status: 'stale', driftCount: 0, rebuiltAt: null },
];
export const EXAMPLE_HEALTH = {
    status: 'ok',
    release: 'dev',
    startedAt: '2026-09-12T18:55:00.000Z',
    now: EXAMPLE_NOW,
    probes: [
        { key: 'database', ok: true, detail: 'SELECT 1 in 2 ms', observedAt: EXAMPLE_NOW },
        { key: 'migrations', ok: true, detail: 'no pending migration', observedAt: EXAMPLE_NOW },
        { key: 'pgboss', ok: true, detail: 'schema at the pinned version', observedAt: EXAMPLE_NOW },
        { key: 'worker', ok: true, detail: 'heartbeat 12 s ago', observedAt: EXAMPLE_NOW },
        { key: 'disk', ok: true, detail: '61% free', observedAt: EXAMPLE_NOW },
    ],
    derived: [],
};
export const EXAMPLE_PING = {
    jobId: '01923b6e-8f00-7a00-8000-0000000000a1',
    roundTripMs: 42,
};
/** An evidence bundle id and a file inside it. */
export const EXAMPLE_EVIDENCE = {
    params: { id: '2026-09-12-design', '*': 'index.html' },
    response: {
        contentType: 'text/html; charset=utf-8',
        body: '<!doctype html><title>Evidence</title><h1>Evidence</h1>',
    },
};
/**
 * Stand-ins for the ids that only an example answer names: a row a create makes, an entry inside a
 * `jsonb` column. They carry `EXAMPLE_NOW` like `EXAMPLE_IDS`, but their last group starts
 * `0000000f`, a range neither `EXAMPLE_IDS` (`0000000e`) nor 1.0a's demo ids (`0000000d`) use, so
 * no demo layer writes a row at one and no request names one.
 */
export const EXAMPLE_ANSWER_IDS = Object.freeze({
    typeCreated: '01a096fd-a380-7000-8000-0000000f0201',
    fieldCreated: '01a096fd-a380-7000-8000-0000000f0301',
    templateCreated: '01a096fd-a380-7000-8000-0000000f0401',
    linkCreated: '01a096fd-a380-7000-8000-0000000f0501',
    linkTypeCreated: '01a096fd-a380-7000-8000-0000000f0502',
    // CS r3: the dated link `EXAMPLE_LINK_DATED` makes.
    linkDated: '01a096fd-a380-7000-8000-0000000f0503',
    sidebarItemCreated: '01a096fd-a380-7000-8000-0000000f0601',
    // CS r4b: the view `views.create` makes (saved views, 14xx).
    savedViewCreated: '01a096fd-a380-7000-8000-0000000f1401',
    exportFile: '01a096fd-a380-7000-8000-0000000f0801',
    // Chronology (0axx): time points, placements, constraints, advances and states.
    timePointSaltStart: '01a096fd-a380-7000-8000-0000000f0a01',
    timePointSaltEnd: '01a096fd-a380-7000-8000-0000000f0a02',
    timePointCreated: '01a096fd-a380-7000-8000-0000000f0a03',
    timePointClockOrigin: '01a096fd-a380-7000-8000-0000000f0a04',
    placementBells: '01a096fd-a380-7000-8000-0000000f0a05',
    placementCreated: '01a096fd-a380-7000-8000-0000000f0a06',
    placementSession2: '01a096fd-a380-7000-8000-0000000f0a07',
    constraintCreated: '01a096fd-a380-7000-8000-0000000f0a08',
    clockAdvanceCreated: '01a096fd-a380-7000-8000-0000000f0a09',
    stateCreated: '01a096fd-a380-7000-8000-0000000f0a0a',
    // The undated `session` placements the session examples carry (CS r2c, 1.T item T2).
    placementSession3: '01a096fd-a380-7000-8000-0000000f0a0b',
    placementSessionCreated: '01a096fd-a380-7000-8000-0000000f0a0c',
    // The ledger, sessions and the Inbox (0cxx).
    secretMaelis: '01a096fd-a380-7000-8000-0000000f0c01',
    secretMaelisBlock: '01a096fd-a380-7000-8000-0000000f0c02',
    secretS3: '01a096fd-a380-7000-8000-0000000f0c03',
    knowledgeCreated: '01a096fd-a380-7000-8000-0000000f0c04',
    knowledgeRetracted: '01a096fd-a380-7000-8000-0000000f0c05',
    playMissingLamplighters: '01a096fd-a380-7000-8000-0000000f0c06',
    playCreated: '01a096fd-a380-7000-8000-0000000f0c07',
    sessionCreated: '01a096fd-a380-7000-8000-0000000f0c08',
    captureCreated: '01a096fd-a380-7000-8000-0000000f0c09',
    // CS r5a: the session examples' answers (a created item and capture, Finish's next session).
    sessionItemCreated: '01a096fd-a380-7000-8000-0000000f0c0a',
    sessionCaptureCreated: '01a096fd-a380-7000-8000-0000000f0c0b',
    sessionNext: '01a096fd-a380-7000-8000-0000000f0c0c',
    placementSessionNext: '01a096fd-a380-7000-8000-0000000f0a0d',
    sessionItemSecret: '01a096fd-a380-7000-8000-0000000f0c0d',
    strongStartBlock: '01a096fd-a380-7000-8000-0000000f0c0e',
    // Sheets and variants (0dxx). 0d02 (the stand-in Ghoul's sheet) is retired: the SRD Ghoul's
    // primary sheet is `EXAMPLE_IDS.sheetReferenceGhoul` since CS r3a.
    sheetCreated: '01a096fd-a380-7000-8000-0000000f0d01',
    variantCreated: '01a096fd-a380-7000-8000-0000000f0d03',
    variantSheetCreated: '01a096fd-a380-7000-8000-0000000f0d04',
    // CS r5b: batches, sources, promotes, bootstrap and undo (15xx). `changeBatch` is the id the
    // mock names in `CHANGE_BATCH_HEADER` on every marked success.
    changeBatch: '01a096fd-a380-7000-8000-0000000f1501',
    undoBatch: '01a096fd-a380-7000-8000-0000000f1502',
    bootstrapBatch: '01a096fd-a380-7000-8000-0000000f1503',
    sessionSourceCreated: '01a096fd-a380-7000-8000-0000000f1504',
    sourceAssetCreated: '01a096fd-a380-7000-8000-0000000f1505',
    promotedStub: '01a096fd-a380-7000-8000-0000000f1506',
    // CS r6a: the Foundry import's answers (16xx) and the Acolyte's Foundry sheet (0dxx).
    foundryAssetCreated: '01a096fd-a380-7000-8000-0000000f1601',
    actorImportCreated: '01a096fd-a380-7000-8000-0000000f1602',
    actorImportGhoulLinked: '01a096fd-a380-7000-8000-0000000f1603',
    sheetAcolyteFoundry: '01a096fd-a380-7000-8000-0000000f0d05',
    // CS r6c: the canon page a card variant makes (0dxx).
    variantSpellCreated: '01a096fd-a380-7000-8000-0000000f0d06',
});
//# sourceMappingURL=examples.js.map